import type { BadgeSize, BadgeVariant, IconName, TabProps } from '@meterup/atto';
import { Badge } from '@meterup/atto';
import { useOuiDataMap } from '@meterup/common';

import { paths } from '../../../constants';
import { CveSeverity, PermissionType } from '../../../gql/graphql';
import { useNetwork } from '../../../hooks/useNetworkFromPath';
import { NosFeature } from '../../../hooks/useNosFeatures';
import { useCurrentCompany } from '../../../providers/CurrentCompanyProvider';
import { manufacturerIconName } from '../../../utils/clientLists';
import { makeLink } from '../../../utils/main_and_drawer_navigation';
import { useInsightsCrumbs } from '../../../utils/routing';
import { screamingSnakeOrKebabCaseToWords } from '../../../utils/strings';
import { useGetAccessLevelForUser } from '../../HasAccess/useAccessLevelForUser';
import { ReactRouterLink } from '../../ReactRouterLink';
import { clientNameOrNull, useClientByMAC } from './utils2';

export enum ClientsTab {
  Devices = 'devices',
  Vulnerabilities = 'vulnerabilities',
  ConnectionEvents = 'connection-events',
  Blocked = 'blocked',
}

export enum ClientsVulnerabilitiesTab {
  CVEs = 'cves',
  CPEs = 'cpes',
}

export enum ClientTab {
  ConnectionEvents = 'connection-events',
  Insights = 'insights',
  Vulnerabilities = 'vulnerabilities',
}

export enum ClientVulnerabilitiesTab {
  CVEs = 'cves',
  CPEs = 'cpes',
}

export function useClientCrumbs({ macAddress }: { macAddress: string }) {
  const companyName = useCurrentCompany();
  const network = useNetwork();
  const insightsCrumb = useInsightsCrumbs();
  const ouiData = useOuiDataMap();
  const client = useClientByMAC(macAddress);

  return [
    ...insightsCrumb,
    {
      page: {
        as: ReactRouterLink,
        path: paths.pages.ClientsListPage,
        to: makeLink(paths.pages.ClientsListPage, {
          companyName,
          networkSlug: network.slug,
        }),
        label: 'Clients',
      },
    },
    {
      page: {
        as: ReactRouterLink,
        path: paths.pages.ClientPage,
        to: makeLink(paths.pages.ClientPage, {
          companyName,
          networkSlug: network.slug,
          macAddress,
          tab: ClientTab.Insights,
        }),
        icon: client ? manufacturerIconName(client, ouiData) : 'unknown',
        label: (client ? clientNameOrNull(client) : undefined) ?? client?.macAddress ?? '-',
      },
    },
  ];
}

export function useClientsSubtabs({
  activeTab,
}: {
  activeTab?: ClientsVulnerabilitiesTab;
}): TabProps[] {
  const companyName = useCurrentCompany();
  const network = useNetwork();
  const getAccessLevel = useGetAccessLevelForUser();
  const cvesAccessLevel = getAccessLevel({
    operatorsSkipFeatureFlags: false,
    permissions: [PermissionType.PermNetworkDevicesRead, PermissionType.PermCpeRead],
    anyFeatureFlags: ['cpe-tab'],
  });
  const cpeAccessLevel = getAccessLevel({
    operatorsSkipFeatureFlags: false,
    permissions: [PermissionType.PermCpeRead],
    featureFlags: 'cpe-tab',
  });

  return [
    {
      accessLevel: cvesAccessLevel,
      as: ReactRouterLink,
      to: makeLink(paths.pages.ClientsVulnerabilitiesCVEsPage, {
        companyName,
        networkSlug: network.slug,
      }),
      selected: activeTab === ClientsVulnerabilitiesTab.CVEs,
      icon: 'vulnerability' as IconName,
      label: 'CVEs',
      tooltip:
        'CVEs (Common Vulnerabilities and Exposures) are unique identifiers for publicly disclosed cybersecurity vulnerabilities in software or hardware.',
    },
    {
      accessLevel: cpeAccessLevel,
      as: ReactRouterLink,
      to: makeLink(paths.pages.ClientsVulnerabilitiesCPEsPage, {
        companyName,
        networkSlug: network.slug,
      }),
      selected: activeTab === ClientsVulnerabilitiesTab.CPEs,
      icon: 'parts' as IconName,
      label: 'CPEs',
      tooltip:
        'CPEs (Common Platform Enumeration) are unique identifiers for publicly disclosed cybersecurity vulnerabilities in software or hardware.',
    },
  ];
}

export function useClientsTabs({
  activeTab,
  activeSubtab,
  devicesCount,
  vulnerabilitiesCount,
}: {
  activeTab: ClientsTab;
  activeSubtab?: ClientsVulnerabilitiesTab;
  devicesCount?: number;
  vulnerabilitiesCount?: number;
}): TabProps[] {
  const companyName = useCurrentCompany();
  const network = useNetwork();
  const getAccessLevel = useGetAccessLevelForUser();
  const cvesAccess = getAccessLevel({
    operatorsSkipFeatureFlags: false,
    permissions: [PermissionType.PermNetworkDevicesRead, PermissionType.PermCpeRead],
    anyFeatureFlags: ['cpe-tab'],
  });
  const blocklistAccess = getAccessLevel({
    nosFeatures: [NosFeature.COS_CLIENT_BLOCKLIST],
    featureFlags: 'client-blocklist',
  });

  const subtabs = useClientsSubtabs({ activeTab: activeSubtab });

  return [
    {
      as: ReactRouterLink,
      to: makeLink(paths.pages.ClientsListPage, {
        companyName,
        networkSlug: network.slug,
      }),
      selected: activeTab === ClientsTab.Devices,
      icon: 'client' as IconName,
      label: 'List',
      annotation: devicesCount,
    },
    {
      as: ReactRouterLink,
      to: makeLink(paths.pages.ClientsConnectionEventsPage, {
        companyName,
        networkSlug: network.slug,
      }),
      selected: activeTab === ClientsTab.ConnectionEvents,
      icon: 'connection' as IconName,
      label: 'Connection events',
    },
    {
      accessLevel: blocklistAccess,
      as: ReactRouterLink,
      to: makeLink(paths.pages.ClientsBlockedPage, {
        companyName,
        networkSlug: network.slug,
      }),
      selected: activeTab === ClientsTab.Blocked,
      icon: 'block' as IconName,
      label: 'Blocked',
    },
    {
      accessLevel: cvesAccess,
      as: ReactRouterLink,
      to: makeLink(paths.pages.ClientsVulnerabilitiesCVEsPage, {
        companyName,
        networkSlug: network.slug,
      }),
      selected: activeTab === ClientsTab.Vulnerabilities,
      icon: 'bug' as IconName,
      label: 'Vulnerabilities',
      annotation: vulnerabilitiesCount,
      subtabs,
    },
  ];
}

export function CVESeverityBadge({
  severity,
  size = 'small',
}: {
  severity: CveSeverity;
  size?: BadgeSize;
}) {
  let icon: IconName = 'priority-low';
  let variant: BadgeVariant = 'positive';
  if (severity === CveSeverity.Critical) {
    icon = 'priority-highest';
    variant = 'negative';
  } else if (severity === CveSeverity.High) {
    icon = 'priority-high';
    variant = 'attention';
  } else if (severity === CveSeverity.Medium) {
    icon = 'priority-medium';
    variant = 'alternative';
  } else if (severity === CveSeverity.Low) {
    icon = 'priority-low';
    variant = 'neutral';
  }

  return (
    <Badge arrangement="leading-icon" ends="card" icon={icon} size={size} variant={variant}>
      {screamingSnakeOrKebabCaseToWords(severity)}
    </Badge>
  );
}
